/**
 * Encarteirados Modal
 *
 * Shows booked-but-not-invoiced jobs split by category (Promo / Digital),
 * with the ability to add new entries. Styled to match TrendExplanationModal.
 */

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { SensitiveValue } from '@/components/privacy/SensitiveText'
import { formatCurrency } from '@/lib/financial-utils'
import { Plus } from 'lucide-react'
import { useClientsDropdown } from '@/hooks/sig/useClients'
import type { EncarteiradoJob } from '@/lib/services/encarteirados.service'

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

interface EncarteiradosModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  jobs: EncarteiradoJob[]
}

function JobTable({ jobs }: { jobs: EncarteiradoJob[] }) {
  const total = jobs.reduce((s, j) => s + j.value, 0)

  if (jobs.length === 0) {
    return (
      <p className="text-brand-gray text-sm py-6 text-center">
        Nenhum job encarteirado nesta categoria.
      </p>
    )
  }

  return (
    <div className="border border-brand-dark-lighter rounded-lg overflow-hidden">
      <table className="w-full text-[11px] sm:text-xs">
        <thead className="bg-brand-dark">
          <tr>
            <th className="text-left px-3 py-2 text-brand-gray font-medium">Job</th>
            <th className="text-left px-3 py-2 text-brand-gray font-medium">Cliente</th>
            <th className="text-left px-3 py-2 text-brand-gray font-medium">PIT</th>
            <th className="text-left px-3 py-2 text-brand-gray font-medium">Mês previsto</th>
            <th className="text-right px-3 py-2 text-brand-gray font-medium">Valor</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.id} className="border-t border-brand-dark-lighter">
              <td className="px-3 py-2 font-medium text-white">{job.job}</td>
              <td className="px-3 py-2 text-brand-gray-light">{job.client}</td>
              <td className="px-3 py-2 text-brand-gray-light font-mono">{job.pit ?? '—'}</td>
              <td className="px-3 py-2 text-brand-gray-light">{MONTH_LABELS[job.mesPrevisto - 1]}</td>
              <td className="px-3 py-2 text-right text-white font-semibold tabular-nums">
                <SensitiveValue mask="R$ ••••••">
                  {formatCurrency(job.value)}
                </SensitiveValue>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-brand-dark-lighter bg-brand-dark/40">
            <td colSpan={4} className="px-3 py-2 font-bold text-white">Subtotal</td>
            <td className="px-3 py-2 text-right text-brand-primary font-bold tabular-nums">
              <SensitiveValue mask="R$ ••••••">
                {formatCurrency(total)}
              </SensitiveValue>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function AddJobButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 transition-colors whitespace-nowrap flex-shrink-0"
    >
      <Plus size={14} />
      Adicionar encarteirado
    </button>
  )
}

function AddJobModal({
  open,
  onOpenChange,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdd: (job: Omit<EncarteiradoJob, 'id'>) => void
}) {
  const { clients, loading: clientsLoading } = useClientsDropdown()
  const [job, setJob] = useState('')
  const [client, setClient] = useState('')
  const [pit, setPit] = useState('')
  const [mesPrevisto, setMesPrevisto] = useState(new Date().getMonth() + 1)
  const [value, setValue] = useState('')
  const [category, setCategory] = useState<'promo' | 'digital'>('promo')

  /** Format number string with dot separators (pt-BR thousands) as the user types */
  const handleValueChange = (raw: string) => {
    // Strip everything except digits and comma (decimal separator)
    const cleaned = raw.replace(/[^\d,]/g, '')
    const [intPart, ...decParts] = cleaned.split(',')
    const digits = intPart.replace(/^0+(?=\d)/, '') // strip leading zeros
    // Insert dots every 3 digits from the right
    const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    setValue(decParts.length > 0 ? `${formatted},${decParts.join('')}` : formatted)
  }

  const canSubmit = job.trim().length > 0 && client.length > 0 && value.trim().length > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const numValue = parseFloat(value.replace(/\./g, '').replace(',', '.'))
    if (!job.trim() || !client || isNaN(numValue) || numValue <= 0) return
    onAdd({ job: job.trim(), client, pit: pit.trim() || undefined, mesPrevisto, value: numValue, category })
    setJob('')
    setClient('')
    setPit('')
    setValue('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg bg-brand-dark-light border-brand-dark-lighter text-white"
        overlayClassName="bg-black/80"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-display text-xl">
            Novo Encarteirado
          </DialogTitle>
          <DialogDescription className="text-brand-gray-light">
            Adicione um job encarteirado para acompanhar receita futura.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-[10px] font-medium text-brand-gray uppercase tracking-wide mb-1">Job *</label>
              <input
                type="text"
                value={job}
                onChange={(e) => setJob(e.target.value)}
                placeholder="Nome do job"
                className="w-full px-3 py-2 rounded-lg bg-brand-dark border border-brand-dark-lighter text-white text-sm placeholder:text-brand-gray focus:border-brand-primary focus:outline-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] font-medium text-brand-gray uppercase tracking-wide mb-1">Cliente *</label>
              <select
                value={client}
                onChange={(e) => setClient(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-brand-dark border border-brand-dark-lighter text-white text-sm focus:border-brand-primary focus:outline-none appearance-none"
              >
                <option value="" className="bg-brand-dark text-brand-gray">
                  {clientsLoading ? 'Carregando clientes...' : 'Selecione um cliente'}
                </option>
                {clients.map((c) => (
                  <option key={c.id} value={c.nomeFantasia || c.nome} className="bg-brand-dark">
                    {c.nomeFantasia || c.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-brand-gray uppercase tracking-wide mb-1">PIT</label>
              <input
                type="text"
                value={pit}
                onChange={(e) => setPit(e.target.value)}
                placeholder="PIT-2026-000"
                className="w-full px-3 py-2 rounded-lg bg-brand-dark border border-brand-dark-lighter text-white text-sm placeholder:text-brand-gray focus:border-brand-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-brand-gray uppercase tracking-wide mb-1">Mês previsto *</label>
              <select
                value={mesPrevisto}
                onChange={(e) => setMesPrevisto(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg bg-brand-dark border border-brand-dark-lighter text-white text-sm focus:border-brand-primary focus:outline-none appearance-none"
              >
                {MONTH_LABELS.map((label, i) => (
                  <option key={i} value={i + 1} className="bg-brand-dark">
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-brand-gray uppercase tracking-wide mb-1">Valor (R$) *</label>
              <input
                type="text"
                value={value}
                onChange={(e) => handleValueChange(e.target.value)}
                placeholder="350.000"
                className="w-full px-3 py-2 rounded-lg bg-brand-dark border border-brand-dark-lighter text-white text-sm placeholder:text-brand-gray focus:border-brand-primary focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-medium text-brand-gray uppercase tracking-wide mb-1.5">Categoria *</label>
            <div className="flex gap-2">
              {(['promo', 'digital'] as const).map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    category === cat
                      ? 'bg-brand-primary text-brand-dark'
                      : 'bg-brand-dark-lighter text-brand-gray hover:text-white'
                  }`}
                >
                  {cat === 'promo' ? 'Promo' : 'Digital'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-brand-gray hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-primary text-brand-dark hover:bg-brand-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Adicionar
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function EncarteiradosModal({ open, onOpenChange, jobs }: EncarteiradosModalProps) {
  const [addModalOpen, setAddModalOpen] = useState(false)
  const promoJobs = jobs.filter((j) => j.category === 'promo')
  const digitalJobs = jobs.filter((j) => j.category === 'digital')
  const total = jobs.reduce((s, j) => s + j.value, 0)

  const handleAddJob = (_job: Omit<EncarteiradoJob, 'id'>) => {
    // TODO(api): call mutation to persist new job
    // For now this is a no-op — the mock data is static
    setAddModalOpen(false)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-4xl bg-brand-dark-light border-brand-dark-lighter text-white"
          overlayClassName="bg-black/80"
        >
          <DialogHeader>
            <DialogTitle className="text-white font-display text-xl">
              Receita Encarteirada
            </DialogTitle>
            <div className="flex items-center justify-between gap-4">
              <DialogDescription className="text-brand-gray-light">
                Receitas já mapeadas mas ainda não faturadas. Total:{' '}
                <span className="text-brand-primary font-semibold">
                  <SensitiveValue mask="R$ ••••••">
                    {formatCurrency(total)}
                  </SensitiveValue>
                </span>
              </DialogDescription>
              <AddJobButton onClick={() => setAddModalOpen(true)} />
            </div>
          </DialogHeader>

          <div className="space-y-4 text-sm mt-2">
            <Tabs defaultValue="promo">
              <TabsList>
                <TabsTrigger value="promo">
                  Promo ({promoJobs.length})
                </TabsTrigger>
                <TabsTrigger value="digital">
                  Digital ({digitalJobs.length})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="promo">
                <JobTable jobs={promoJobs} />
              </TabsContent>
              <TabsContent value="digital">
                <JobTable jobs={digitalJobs} />
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      <AddJobModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        onAdd={handleAddJob}
      />
    </>
  )
}
